/**
 * Session playback position for lesson videos.
 * localStorage is the in-browser source of truth while watching;
 * Firestore is flushed separately on pause / hide / leave.
 */

const MIN_RESUME_SECONDS = 2;
const NEAR_END_SECONDS = 5;

/**
 * @param {string} userId
 * @param {string} lessonId
 * @returns {string}
 */
export const videoPositionStorageKey = (userId, lessonId) =>
  `videoPosition_${userId}_${lessonId}`;

/**
 * Read the last locally saved playback position for a lesson.
 * @param {string} userId
 * @param {string} lessonId
 * @returns {{ seconds: number, duration: number, savedAt: number }|null}
 */
export const readLocalVideoPosition = (userId, lessonId) => {
  if (!userId || !lessonId || typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(videoPositionStorageKey(userId, lessonId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.seconds !== 'number' || Number.isNaN(parsed.seconds)) {
      return null;
    }
    return {
      seconds: parsed.seconds,
      duration: typeof parsed.duration === 'number' ? parsed.duration : 0,
      savedAt: typeof parsed.savedAt === 'number' ? parsed.savedAt : 0
    };
  } catch {
    return null;
  }
};

/**
 * Persist playback position locally (throttled by the caller).
 * @param {string} userId
 * @param {string} lessonId
 * @param {number} seconds
 * @param {number} [duration]
 */
export const writeLocalVideoPosition = (userId, lessonId, seconds, duration = 0) => {
  if (!userId || !lessonId || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(
      videoPositionStorageKey(userId, lessonId),
      JSON.stringify({
        seconds,
        duration,
        savedAt: Date.now()
      })
    );
  } catch {
    // Ignore quota / private-mode failures; Firestore flush is the fallback.
  }
};

/**
 * Choose a resume offset from localStorage vs Firestore, then clamp near start/end.
 * Prefers localStorage when it is newer than the Firestore timestamp.
 *
 * @param {Object} options
 * @param {{ seconds: number, savedAt: number }|null} options.local
 * @param {number} [options.firestoreSeconds]
 * @param {string} [options.firestoreUpdatedAt] - ISO timestamp
 * @param {number} [options.duration]
 * @returns {number}
 */
export const resolveResumeSeconds = ({
  local,
  firestoreSeconds,
  firestoreUpdatedAt,
  duration
} = {}) => {
  const localSeconds = local && typeof local.seconds === 'number' ? local.seconds : null;
  const localAt = local?.savedAt ?? 0;
  const firestoreAt = firestoreUpdatedAt ? Date.parse(firestoreUpdatedAt) : 0;
  const firestoreValid = Number.isFinite(firestoreAt) ? firestoreAt : 0;

  let start = 0;
  const localIsNewer =
    localSeconds != null && (!firestoreUpdatedAt || localAt >= firestoreValid);

  if (localIsNewer) {
    start = localSeconds;
  } else if (typeof firestoreSeconds === 'number' && !Number.isNaN(firestoreSeconds)) {
    start = firestoreSeconds;
  }

  if (start <= MIN_RESUME_SECONDS) return 0;
  if (typeof duration === 'number' && duration > 0 && start >= duration - NEAR_END_SECONDS) {
    return 0;
  }
  return start;
};
